import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import Icon from "@ui-kit/icons/Icon.web";
import {
  BannerSharedProps,
  BannerVariant,
  BannerVariantType,
} from "./Banner.props";

export const getColors = (variant?: BannerVariantType) => {
  switch (variant) {
    case BannerVariant.Success:
      return { background: "green.200", color: "green.800" };
    case BannerVariant.Warning:
      return { background: "yellow.200", color: "yellow.800" };
    case BannerVariant.DarkBlue:
      return { background: "blue.500", color: "white" };
    case BannerVariant.Error:
      return { background: "red.200", color: "red.800" };
    case BannerVariant.Info:
      return { background: "blue.200", color: "blue.800" };
    default:
      return { background: "gray.200", color: "gray.800" };
  }
};

export default function Banner({
  variant,
  isRounded,
  icon,
  message,
  color,
  bgColor,
  ...rest
}: BannerSharedProps): JSX.Element {
  const colors = getColors(variant);

  const selectedBgColor = bgColor || colors.background;
  const selectedColor = color || colors.color;
  const testId = variant ? `banner-${variant}` : "banner-custom-colors";

  return (
    <Flex
      data-testid={testId}
      padding="xs"
      borderRadius={isRounded ? "sm" : 0}
      alignItems="center"
      justifyContent="center"
      w="full"
      role="status"
      {...rest}
      backgroundColor={selectedBgColor}
      color={selectedColor}
      css={{ "& *": { color: "inherit" } }}
    >
      {icon && (
        <Icon
          outline
          name={icon}
          h="element.md"
          w="element.md"
          color={selectedColor}
          marginRight="2xs"
        />
      )}
      {typeof message === "string" ? <Text size="sm">{message}</Text> : message}
    </Flex>
  );
}

export type BannerProps = BannerSharedProps;

Banner.Warning = function BannerWarning({
  ...rest
}: BannerSharedProps): JSX.Element {
  return <Banner variant="warning" icon="Warning" {...rest} />;
};

Banner.Success = function BannerSuccess({
  ...rest
}: BannerSharedProps): JSX.Element {
  return <Banner variant="success" icon="TickCircle" {...rest} />;
};

Banner.Error = function BannerError({
  ...rest
}: BannerSharedProps): JSX.Element {
  return <Banner variant="error" icon="Error" {...rest} />;
};

Banner.Info = function BannerInfo({ ...rest }: BannerSharedProps): JSX.Element {
  return <Banner variant="info" icon="Info" {...rest} />;
};
